import React from "react";
import "./AppCard.scss";

/**
 * AppCard — Reusable card component matching UX_DESIGNS card.tsx.
 *
 * Exports: AppCard, AppCardHeader, AppCardTitle, AppCardDescription,
 *          AppCardContent, AppCardFooter
 *
 * All components accept className and spread remaining props.
 *
 * Props:
 *   hover           — adds the lift-on-hover treatment
 *   overflowVisible — turn OFF the card's default `overflow: hidden`.
 *   as              — render as a different element. The card is a container, and
 *                     a container often has a name in HTML: a plan card is an
 *                     <article>, a banner is a <section>. Same prop AppButton
 *                     already takes, so there is one spelling for this in the
 *                     library.
 *
 * The card clips its content by default so anything bleeding to the edge (a
 * table, an image) follows the rounded corners. That same clip also swallows
 * absolutely-positioned children, so a dropdown or popover rendered inside a
 * card is cut off at the card's boundary. Pass `overflowVisible` on cards that
 * contain one. It is opt-in rather than the default because turning clipping
 * off globally would let edge-to-edge content escape the corner radius on every
 * card in the app.
 */

const AppCard = React.forwardRef((
  { className = "", children, hover = false, overflowVisible = false, as: Comp = "div", ...props },
  ref,
) => (
  <Comp
    ref={ref}
    className={
      `app-card${hover ? " app-card--hover" : ""}`
      + `${overflowVisible ? " app-card--overflow-visible" : ""}`
      + `${className ? ` ${className}` : ""}`
    }
    {...props}
  >
    {children}
  </Comp>
));
AppCard.displayName = "AppCard";

const AppCardHeader = React.forwardRef(({ className = "", children, ...props }, ref) => (
  <div ref={ref} className={`app-card__header ${className}`} {...props}>
    {children}
  </div>
));
AppCardHeader.displayName = "AppCardHeader";

const AppCardTitle = React.forwardRef(({ className = "", children, as: Tag = "h3", ...props }, ref) => (
  <Tag ref={ref} className={`app-card__title ${className}`} {...props}>
    {children}
  </Tag>
));
AppCardTitle.displayName = "AppCardTitle";

/**
 * The one line under a card title. It is a spec'd part of the card, not free
 * text: 13px, regular, secondary ink. Every card that explains itself did so in
 * its own stylesheet before this existed, which is why no two agreed on the size.
 */
const AppCardDescription = React.forwardRef(({ className = "", children, as: Tag = "p", ...props }, ref) => (
  <Tag ref={ref} className={`app-card__description${className ? ` ${className}` : ""}`} {...props}>
    {children}
  </Tag>
));
AppCardDescription.displayName = "AppCardDescription";

const AppCardContent = React.forwardRef((
  { className = "", children, overflowVisible = false, ...props },
  ref,
) => (
  <div
    ref={ref}
    className={`app-card__content${overflowVisible ? " app-card__content--overflow-visible" : ""}${className ? ` ${className}` : ""}`}
    {...props}
  >
    {children}
  </div>
));
AppCardContent.displayName = "AppCardContent";

const AppCardFooter = React.forwardRef(({ className = "", children, ...props }, ref) => (
  <div ref={ref} className={`app-card__footer ${className}`} {...props}>
    {children}
  </div>
));
AppCardFooter.displayName = "AppCardFooter";

export { AppCard, AppCardHeader, AppCardTitle, AppCardDescription, AppCardContent, AppCardFooter };
export default AppCard;
